<!--
    AssetImageManager

    DESCRIPTION
    a component for viewing asset media

    Asset media comes from the asset store
-->

<template>

    <div v-if="state.asset_media.length == 0">
        <EmptyList :heading="`No ${file_noun}`" :message="`This ${asset_noun} doesn't have any ${file_noun} yet.`" />
    </div>

    <draggable
        v-if="state.asset_media.length > 0"
        v-model="state.asset_media"
        item-key="id"
        @end="onSortEnd"
    >
        <template #item="{element, index}" class="overflow-hidden rounded-md bg-white p-2 border border-gray-200">
            <div class="w-full mb-2">
                <AssetImageManagerCompactItem :media="element" :asset_id="asset_id" :media_types="media_types" v-on:refresh="getAssetMedia()" />
            </div>
        </template>
    </draggable>

    <div class="flex justify-end w-full pt-6">
        <ButtonConfirm
            ref="delete_all_asset_images_btn"
            v-if="user_is_auctioneer && state.asset_media.length > 0"
            :trigger_button_color="'text-red'"
            :trigger_button_width="'w-auto'"
            :trigger_button_disabled="false"
            :trigger_button_icon="null"
            :trigger_button_class="null"
            :trigger_button_text="'Delete all photos'"
            :confirm_button_color="'red'"
            :confirm_button_disabled="false"
            :confirm_button_icon="null"
            :confirm_button_class="null"
            :confirm_button_text="'Yes, delete all photos'"
            :cancel_button_text="'Back'"
            :no_cancel="false"
            :buttons_disabled="false"
            :loading="false"
            :onConfirm="deleteAllAssetImages"
            heading="Delete all photos?"
            :body="`Are you sure that you want to delete all this vehicle's photos?`"
        />
        <Button v-if="state.asset_media.length > 0" width="w-auto" :onClick="() => save()">Save new order</Button>
    </div>

</template>

<script>
import { useAssetStore } from '@sto/asset.store'
import { useSystemStore } from '@sto/system.store'
import { onBeforeMount, watch, computed, reactive, ref } from 'vue';
import draggable from 'vuedraggable'

import AssetImageManagerCompactItem from '@par/asset/AssetImageManagerCompactItem.vue'
import ButtonConfirm from "@glo/ButtonConfirm.vue";

export default {
    components: {
        ButtonConfirm,
        draggable,
        AssetImageManagerCompactItem
    },
    props: ['asset_id','asset_type','media_types', 'user_is_auctioneer', 'refresh'],
    setup(props) {
        const ASSET_STORE = useAssetStore()
        const SYSTEM_STORE = useSystemStore()

        const delete_all_asset_images_btn = ref(null)

        const state = reactive({
            asset_media: [],
            store_asset_media: null,
            loading: false
        })

        const getAssetMedia = async () => {

            if (props.asset_id) {
                await ASSET_STORE.getMedia(props.asset_id, props.media_types)
                state.store_asset_media = ASSET_STORE.getterAssetMedia(props.asset_id)
                state.asset_media = state.store_asset_media?.image ? state.store_asset_media.image : []
            }
        }

        const asset_images = computed(() => {
            return state.store_asset_media && state.store_asset_media.image ? state.store_asset_media.image : []
        })

        const file_noun = computed(() => {
            if (!Array.isArray(props.media_types) || props.media_types.length > 1) return 'media'

            switch(props.media_types[0]) {
                case 'image':
                    return 'photos'
                default:
                    return 'media'
            }
        })

        const asset_noun = computed(() => {
            switch (props.asset_type) {
                case 'general':
                    return 'item'
                case 'vehicle':
                default:
                    return 'vehicle'
            }
        })
        
        const getNewOrderPayload = () => {
            let new_order_data = {}

            state.asset_media.forEach(media => {
                new_order_data[media.id] = media.sort_order
            })
            
            return new_order_data
        }
        
        const storeNewMediaOrder = () => {
            const new_order_data = getNewOrderPayload()
            return ASSET_STORE.updateMediaSortOrders(new_order_data)
        }

        const save = async () => {
            if (state.loading) return

            try {
                state.loading = true

                await storeNewMediaOrder()

                SYSTEM_STORE.addSuccess('New order saved')
                getAssetMedia()

            } catch (error) {
                console.error('ASSET_STORE.updateMediaSortOrders error', error)
                SYSTEM_STORE.addError('Error', error?.message || 'There was an error saving the new order')

            } finally {
                state.loading = false
            }
        }

        const onSortEnd = event => {
            state.asset_media.forEach((item, index) => {
                item.sort_order = index;
            });
        }

        const deleteAllAssetImages = async () => {
            try {
                await ASSET_STORE.deleteAllForAsset(props.asset_id, { type: 'image' })
                SYSTEM_STORE.addSuccess('Asset images deleted successfully')
                getAssetMedia()

            } catch (error) {
                console.error(error)
                SYSTEM_STORE.addError('Error', error?.message || 'There was an error deleting asset images')

            } finally {
                delete_all_asset_images_btn.value.closeModal()
            }
        }

        watch(
            () => props.asset_id,
            (new_value, old_value) => {
                getAssetMedia()
            }
        )

        watch(
            () => asset_images.value.length,
            () => {
                state.asset_media = state.store_asset_media?.image ? state.store_asset_media.image : []
            }
        )

        onBeforeMount(() => {
            getAssetMedia()
        })

        return {
            state,
            asset_images,
            file_noun,
            asset_noun,
            getAssetMedia,
            storeNewMediaOrder,
            onSortEnd,
            save,
            deleteAllAssetImages,
            delete_all_asset_images_btn
        }
    },
}
</script>